import { numberParserService } from "./number-parser.service";
import { 
    ITimezoneService, 
    MonthMap, 
    DayMap, 
    TimezoneRule, 
    TimezoneZone,
    ZonesDictionary,
    RulesDictionary,
    ZoneAndRuleInfo
} from "../models/timezone";

// Constants
const NUMBER = "number";
const STRING = "string";

/**
 * Service for timezone conversions
 */
class TimezoneService implements ITimezoneService {
    /**
     * Timezone zone definitions - populated externally
     */
    zones: ZonesDictionary = {};

    /**
     * Timezone rule definitions - populated externally
     */
    rules: RulesDictionary = {};

    /**
     * Month name to index mapping
     */
    private months: MonthMap = { 
        Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, 
        Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 
    };

    /**
     * Day name to index mapping
     */
    private days: DayMap = { 
        Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 
    };

    /**
     * Get the UTC offset for a given time in a timezone
     */
    offset(utcTime: number | Date, timezone: string): number {
        if (timezone === "Etc/UTC" || timezone === "Etc/GMT") {
            return 0;
        }

        const info = this.zoneAndRule(utcTime, this.zones, this.rules, timezone);
        const zone = info.zone;
        const rule = info.rule;

        return numberParserService.parseFloat(rule ? (zone[0] as number) - (rule[6] as number) : zone[0] as number);
    }

    /**
     * Get the timezone abbreviation for a given time
     */
    abbr(utcTime: number | Date, timezone: string): string {
        const info = this.zoneAndRule(utcTime, this.zones, this.rules, timezone);
        const zone = info.zone;
        const rule = info.rule;

        const base = zone[2] as string;

        if (base.indexOf("/") >= 0) {
            return base.split("/")[rule && +rule[6] ? 1 : 0];
        } else if (base.indexOf("%s") >= 0) {
            return base.replace("%s", (!rule || rule[7] === "-") ? '' : rule[7] as string);
        }

        return base;
    }

    /**
     * Convert a date between timezones
     */
    convert(date: Date, fromOffset: string | number, toOffset: string | number): Date {
        let tempToOffset = toOffset;
        let diff: number;

        if (typeof fromOffset === STRING) {
            fromOffset = this.offset(date, fromOffset as string);
        }

        if (typeof toOffset === STRING) {
            toOffset = this.offset(date, toOffset as string);
        }

        const fromLocalOffset = date.getTimezoneOffset();

        date = new Date(date.getTime() + ((fromOffset as number) - (toOffset as number)) * 60000);

        const toLocalOffset = date.getTimezoneOffset();

        if (typeof tempToOffset === STRING) {
            tempToOffset = this.offset(date, tempToOffset as string);
        }

        diff = (toLocalOffset - fromLocalOffset) + ((toOffset as number) - (tempToOffset as number));

        return new Date(date.getTime() + diff * 60000);
    }

    /**
     * Apply a timezone to a local date
     */
    apply(date: Date, timezone: string): Date {
        return this.convert(date, date.getTimezoneOffset(), timezone);
    }

    /**
     * Remove timezone adjustment from a date
     */
    remove(date: Date, timezone: string): Date {
        return this.convert(date, timezone, date.getTimezoneOffset());
    }

    /**
     * Convert a UTC timestamp to a local date
     */
    toLocalDate(time: number): Date {
        return this.apply(new Date(time), "Etc/UTC");
    }

    /**
     * Convert a rule to a Date for a specific year
     */
    private ruleToDate(year: number, rule: TimezoneRule): Date {
        let date: Date;
        let targetDay: number;
        let ourDay: number;
        const month = rule[3] as string;
        const on = rule[4] as string | number;
        const time = rule[5] as number[];
        let cache = rule[8] as Record<number, Date>;

        if (!cache) {
            rule[8] = cache = {};
        }

        if (cache[year]) {
            return cache[year];
        }

        if (!isNaN(on as number)) {
            date = new Date(Date.UTC(year, this.months[month], on as number, time[0], time[1], time[2], 0));
        } else if ((on as string).indexOf("last") === 0) {
            date = new Date(Date.UTC(year, this.months[month] + 1, 1, time[0] - 24, time[1], time[2], 0));

            targetDay = this.days[(on as string).substr(4, 3)];
            ourDay = date.getUTCDay();

            date.setUTCDate(date.getUTCDate() + targetDay - ourDay - (targetDay > ourDay ? 7 : 0));
        } else if ((on as string).indexOf(">=") >= 0) {
            date = new Date(Date.UTC(year, this.months[month], parseInt((on as string).substr(5), 10), time[0], time[1], time[2], 0));

            targetDay = this.days[(on as string).substr(0, 3)];
            ourDay = date.getUTCDay();

            date.setUTCDate(date.getUTCDate() + targetDay - ourDay + (targetDay < ourDay ? 7 : 0));
        } else if ((on as string).indexOf("<=") >= 0) {
            date = new Date(Date.UTC(year, this.months[month], parseInt((on as string).substr(5), 10), time[0], time[1], time[2], 0));

            targetDay = this.days[(on as string).substr(0, 3)];
            ourDay = date.getUTCDay();

            date.setUTCDate(date.getUTCDate() + targetDay - ourDay - (targetDay > ourDay ? 7 : 0));
        }

        return cache[year] = date!;
    }

    /**
     * Find the applicable rule for a given UTC time
     */
    private findRule(utcTime: number, rules: RulesDictionary, zone: string): TimezoneRule | null {
        let zoneRules = rules[zone] as TimezoneRule[];

        if (!zoneRules) {
            const time = zone.split(":");
            let offset = 0;

            if (time.length > 1) {
                offset = parseInt(time[0], 10) * 60 + Number(time[1]);
            }

            return [-1000000, 'max', '-', 'Jan', 1, [0, 0, 0], offset, '-'] as TimezoneRule;
        }

        const year = new Date(utcTime).getUTCFullYear();

        // Filter rules applicable to this year
        // Note: Using == for loose equality to match original behavior since rule values may be strings
        zoneRules = zoneRules.filter((rule: TimezoneRule) => {
            const from = rule[0];
            const to = rule[1];

            // eslint-disable-next-line eqeqeq
            return (from as number) <= year && ((to as number) >= year || (from == year && to === "only") || to === "max");
        });

        // Add the utcTime to sort with rules
        const sortArray: (TimezoneRule | number)[] = [...zoneRules, utcTime];

        sortArray.sort((a, b) => {
            let aVal = a as number;
            let bVal = b as number;

            if (typeof a !== "number") {
                aVal = Number(this.ruleToDate(year, a as TimezoneRule));
            }

            if (typeof b !== "number") {
                bVal = Number(this.ruleToDate(year, b as TimezoneRule));
            }

            return aVal - bVal;
        });

        const idx = sortArray.indexOf(utcTime);
        const rule = sortArray[idx - 1] || sortArray[sortArray.length - 1];

        return isNaN(rule as number) ? rule as TimezoneRule : null;
    }

    /**
     * Find the zone definition for a given UTC time
     */
    private findZone(utcTime: number, zones: ZonesDictionary, timezone: string): TimezoneZone {
        let zoneRules = zones[timezone];

        if (typeof zoneRules === "string") {
            zoneRules = zones[zoneRules] as TimezoneZone[];
        }

        if (!zoneRules) {
            throw new Error('Timezone "' + timezone + '" is either incorrect, or kendo.timezones.min.js is not included.');
        }

        const zoneArray = zoneRules as TimezoneZone[];
        let idx: number;

        for (idx = zoneArray.length - 1; idx >= 0; idx--) {
            const until = zoneArray[idx][3];

            if (until && utcTime > until) {
                break;
            }
        }

        const zone = zoneArray[idx + 1];

        if (!zone) {
            throw new Error('Timezone "' + timezone + '" not found on ' + utcTime + ".");
        }

        return zone;
    }

    /**
     * Get zone and rule info for a given UTC time
     */
    private zoneAndRule(utcTime: number | Date, zones: ZonesDictionary, rules: RulesDictionary, timezone: string): ZoneAndRuleInfo {
        if (typeof utcTime !== NUMBER) {
            const date = utcTime as Date;
            utcTime = Date.UTC(date.getFullYear(), date.getMonth(),
                date.getDate(), date.getHours(), date.getMinutes(),
                date.getSeconds(), date.getMilliseconds());
        }

        const zone = this.findZone(utcTime as number, zones, timezone);

        return {
            zone: zone,
            rule: this.findRule(utcTime as number, rules, zone[1] as string)
        };
    }
}

export const timezoneService = new TimezoneService();